import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import OptimizedImage from '@/components/OptimizedImage';
import PageHero from '@/components/PageHero';
import GrainTexture from '@/components/GrainTexture';
import { sendSummerReadNotification } from '@/lib/webhook';
import summerReadCover from '@/assets/downloadsummerread.jpg';
import summerReadCoverWebp from '@/assets/downloadsummerread.webp';
import vormenLos10 from '@/assets/vormen los/Vormen los-10.png';
import vormenLos10Webp from '@/assets/vormen los/Vormen los-10.webp';
import { SEO } from '@/components/SEO';

const formSchema = z.object({
  naam: z.string().min(2, 'Naam moet minimaal 2 tekens bevatten'),
  email: z.string().email('Voer een geldig e-mailadres in'),
  akkoord: z.boolean().refine(val => val === true, 'Je moet akkoord gaan met de voorwaarden')
});

type FormData = z.infer<typeof formSchema>;

const SummerRead = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      naam: '',
      email: '',
      akkoord: false
    }
  });

  const handleDownloadClick = () => {
    setIsModalOpen(true);
  };

  const handleSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      // Send Summer Read specific Slack notification
      const result = await sendSummerReadNotification({
        naam: data.naam,
        email: data.email
      });

      if (result.success) {
        // Close modal and start download
        setIsModalOpen(false);
        
        // Trigger PDF download
        const link = document.createElement('a');
        link.href = '/downloads/summerread.pdf';
        link.download = 'Summer-Read-Systemique.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
          title: "Download gestart!",
          description: "Je Summer Read wordt nu gedownload. Bedankt voor je interesse!"
        });

        form.reset();
      } else {
        // Show error message to user
        console.error('Slack notification failed:', result.error);
        toast({
          title: "Er ging iets mis",
          description: result.error || "Probeer het opnieuw of neem contact met ons op.",
          variant: "destructive",
        });
      }
    } catch (error) {
      // Fallback error handling
      console.error('Unexpected error:', error);
      toast({
        title: "Er ging iets mis",
        description: "Probeer het opnieuw of neem contact met ons op.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Summer Read | 5 systemische leiderschapstips"
        description="Download gratis: 5 laagdrempelige manieren om systemisch werk in te bedden in je leiderschapsstijl. Praktische tips voor effectiever leiderschap."
        canonical="https://organisatieopstellingen.com/summer-read"
      />
      
      <PageHero 
        title="Summer Read" 
        subtitle="5 manieren om systemisch werk in te bedden."
        imageSrc={vormenLos10}
        imageWebpSrc={vormenLos10Webp}
      />

      <section className="py-24 bg-[#FAEBEE] px-6">
        <div className="container mx-auto max-w-4xl grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-3xl text-[#6b4912] mb-4">Gratis Download</h2>
            <p className="text-gray-600 mb-8">Vul je gegevens in en ontvang direct de PDF met 5 systemische leiderschapstips.</p>
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleDownloadClick(); }}>
              <Input placeholder="Voornaam" required />
              <Input type="email" placeholder="E-mailadres" required />
              <Button className="w-full" onClick={handleDownloadClick}>Download PDF</Button>
            </form>
          </div>
          <div className="bg-white p-8 rounded-3xl shadow-lg">
            <h3 className="font-bold text-[#854C73] mb-4">Wat leer je?</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex gap-2"><CheckCircle size={18} className="text-[#854C73] shrink-0"/> Patronen herkennen</li>
              <li className="flex gap-2"><CheckCircle size={18} className="text-[#854C73] shrink-0"/> Interveniëren vanuit de juiste plek</li>
              <li className="flex gap-2"><CheckCircle size={18} className="text-[#854C73] shrink-0"/> Rust en duidelijkheid creëren</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Download Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">Download aanvragen</DialogTitle>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="naam"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Naam *</FormLabel>
                    <FormControl>
                      <Input placeholder="Je volledige naam" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mailadres *</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="je@email.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="akkoord"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm">
                        Graag ontvang ik de download en geef ik toestemming dat ik sporadisch een nieuwsbrief ontvang.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <Button 
                type="submit" 
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Bezig...' : 'Download aanvragen'}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SummerRead;